import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminBookingDetail } from '@/api/hooks/useAdminBookings';
import { useCustomerFlags } from '@/api/hooks/useCustomerFlags';
import CustomerDetailModal from '@/components/admin/CustomerDetailModal';
import { formatCurrency, formatLocalTime, formatLocalDate, formatDateTime } from '@/lib/format';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { User, ClipboardList, DollarSign, KeyRound, Clock } from 'lucide-react';
import type { BookingStatus } from '@/api/types';

const STATUS_COLORS: Record<BookingStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  COMPLETED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-yellow-100 text-yellow-800',
};

const PAYMENT_KEYS: Record<string, string> = {
  STRIPE: 'admin.payment_stripe',
  CASH: 'admin.payment_cash',
  TERMINAL: 'admin.payment_terminal',
  COMP: 'admin.payment_comp',
  PLATFORM_CREDIT: 'admin.payment_platform_credit',
};

type Props = {
  bookingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const FLAG_COLORS: Record<string, string> = {
  BANNED: 'bg-red-500 text-white',
  NO_SHOW_RISK: 'bg-orange-500 text-white',
  REQUIRES_DEPOSIT: 'bg-amber-500 text-white',
  VIP: 'bg-yellow-400 text-black',
  WATCH: 'bg-blue-500 text-white',
};


export default function BookingDetailModal({ bookingId, open, onOpenChange }: Props) {
  const { t, i18n } = useTranslation();
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  const { data: booking, isLoading } = useAdminBookingDetail(bookingId ?? undefined);
  const { data: flags } = useCustomerFlags(booking?.customerId);

  const timelineEvents = booking
    ? [
        booking.createdAt && { label: t('admin.created'), time: booking.createdAt },
        booking.activatedAt && { label: t('admin.session_activated'), time: booking.activatedAt },
        booking.deactivatedAt && { label: t('admin.session_completed'), time: booking.deactivatedAt },
        booking.cancelledAt && { label: t('admin.booking_cancelled'), time: booking.cancelledAt },
      ].filter(Boolean) as { label: string; time: string }[]
    : [];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {t('admin.booking_detail')}
            {booking && (
              <Badge
                variant="secondary"
                className={`text-xs ${STATUS_COLORS[booking.status as BookingStatus] ?? ''}`}
              >
                {t(`booking.status_${booking.status.toLowerCase()}`)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : booking ? (
          <div className="space-y-4">
            {/* Customer */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                  {t('admin.customer')}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{booking.customerName}</span>
                  {flags && flags.map((f) => (
                    <Badge key={f.id} className={`text-xs ${FLAG_COLORS[f.flagType] ?? ''}`}>
                      {f.flagType}
                    </Badge>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">{booking.customerEmail}</p>
                {booking.customerPhone && (
                  <p className="text-sm text-muted-foreground">{booking.customerPhone}</p>
                )}
                <button
                  onClick={() => setCustomerDetailOpen(true)}
                  className="text-xs text-primary hover:underline mt-2 inline-block"
                >
                  {t('admin.manage_flags')} →
                </button>
              </CardContent>
            </Card>

            {/* Booking Summary */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <ClipboardList className="h-3.5 w-3.5" />
                  {t('admin.booking_summary')}
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8 text-sm">
                  <span className="font-medium">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</span>
                  <span>{booking.unitName}</span>
                  <span>{booking.locationName}</span>
                  <span>{booking.playerCount} {t('admin.label_players').toLowerCase()}</span>
                  <span>{formatLocalTime(booking.startTimeLocal, i18n.language)} → {formatLocalTime(booking.endTimeLocal, i18n.language)}</span>
                  <span>{t('admin.minutes_short', { count: booking.duration })}</span>
                  <span>{formatLocalDate(booking.startTimeLocal, i18n.language)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className="border-l-4 border-l-primary">
              <CardContent className="p-4">
                <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <DollarSign className="h-3.5 w-3.5" />
                  {t('admin.price_breakdown')}
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('admin.label_subtotal')}</span>
                    <span className="font-mono">
                      {formatCurrency(
                        booking.totalPrice - (booking.platformFee ?? 0) - (booking.tax ?? 0),
                        booking.currency,
                        i18n.language,
                      )}
                    </span>
                  </div>
                  {booking.addonTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.addons')}</span>
                      <span className="font-mono">{formatCurrency(booking.addonTotal, booking.currency, i18n.language)}</span>
                    </div>
                  )}
                  {booking.platformFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.label_platform_fee')}</span>
                      <span className="font-mono">{formatCurrency(booking.platformFee, booking.currency, i18n.language)}</span>
                    </div>
                  )}
                  {booking.tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.label_tax')}</span>
                      <span className="font-mono">{formatCurrency(booking.tax, booking.currency, i18n.language)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1.5 font-semibold">
                    <span>{t('admin.label_total')}</span>
                    <span className="font-mono">{formatCurrency(booking.totalPrice, booking.currency, i18n.language)}</span>
                  </div>
                  {booking.paymentMethod && (
                    <div className="flex justify-between pt-1">
                      <span className="text-muted-foreground">{t('admin.label_payment')}</span>
                      <span>{t(PAYMENT_KEYS[booking.paymentMethod] ?? 'admin.payment_stripe')}</span>
                    </div>
                  )}
                  {booking.refundStatus && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.label_refund')}</span>
                      <span>{booking.refundStatus}</span>
                    </div>
                  )}
                  {booking.depositAmount != null && booking.depositAmount > 0 && (
                    <div className="flex justify-between border-t pt-1.5">
                      <span className="text-muted-foreground">{t('admin.label_deposit')}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{formatCurrency(booking.depositAmount, booking.currency, i18n.language)}</span>
                        <Badge variant="secondary" className={`text-xs ${
                          booking.depositStatus === 'CHARGED' ? 'bg-amber-100 text-amber-800' :
                          booking.depositStatus === 'REFUNDED' ? 'bg-green-100 text-green-800' :
                          booking.depositStatus === 'FORFEITED' ? 'bg-red-100 text-red-800' : ''
                        }`}>
                          {t(`admin.deposit_${booking.depositStatus?.toLowerCase() ?? 'none'}`)}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Access */}
            {booking.accessCode && (
              <Card className="bg-green-50/50">
                <CardContent className="p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <KeyRound className="h-3.5 w-3.5" />
                    {t('admin.access')}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">{t('admin.label_access_code')}</span>
                    <span className="font-mono text-lg font-bold tracking-widest">{booking.accessCode}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            {timelineEvents.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    {t('admin.timeline')}
                  </div>
                  <div className="relative pl-6 space-y-3">
                    {/* Vertical line */}
                    <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-muted-foreground/20" />
                    {timelineEvents.map((event) => (
                      <div key={event.label} className="relative flex items-start gap-3">
                        <div className="h-4 w-4 rounded-full bg-primary border-2 border-background z-10 mt-0.5 shrink-0" />
                        <div className="flex flex-1 justify-between">
                          <span className="text-sm">{event.label}</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(event.time, i18n.language)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    {booking?.customerId && (
      <CustomerDetailModal
        userId={booking.customerId}
        open={customerDetailOpen}
        onOpenChange={setCustomerDetailOpen}
      />
    )}
    </>
  );
}
